// apps/web/src/pages/spread/SpreadComposer.tsx
//
// Phase 9 Plan 11 Task 2 — top-level Spread Composer page (SPRD-01 / SPRD-02 /
// SPRD-04). Mirrors apps/web/src/pages/author/RecipeEditor.tsx STRUCTURALLY
// (Phase 8's canonical page-level shell): useParams + useNavigate + useLocation,
// TanStack Query for server fetch, useForm with Zod resolver + onBlur mode,
// useEffect re-seed on query load, save mutation with structured handlers,
// SpreadVocabularyProvider/FormProvider/SpreadComposerShell composition.
//
// COMPONENT TREE per UI-SPEC §"Spread Composer shell" lines 130-155:
//
//   <SpreadComposer spreadId?>
//   └── <SpreadVocabularyProvider value={spread.vocabularyOverrides}>
//       └── <FormProvider {...form}>
//           └── <SpreadComposerShell>
//               ├── <SpreadComposerToolbar>
//               └── <SplitPane>
//                   ├── left: <SpreadLeftPanel>
//                   │   ├── <SpreadTabStrip>
//                   │   └── <BatchSetStrip>
//                   └── right: <SpreadRenderPane>
//
// --------------------------------------------------------------------------
// API WIRING (Plan 09-12):
//
//   Plan 09-11 landed the UI shell with structured-error onError handlers
//   already wired. Plan 09-12 fills in the real fetch bodies:
//
//     - GET    /api/spreads/:id  → queryFn uses api() to load + Zod-parse.
//     - POST   /api/spreads      → saveMutation on a brand-new spread.
//     - PUT    /api/spreads/:id  → saveMutation when spreadId is present.
//     - GET    /api/spreads      → SpreadTabStrip queryFn (TanStack Query)
//                                  to populate the tab strip with the user's
//                                  accessible spreads. Falls back to a single-
//                                  tab representation when the list is empty.
//
//   Errors from the api() wrapper carry the HTTP status on err.status, which
//   the existing onError handler maps to 401/403/404/422/5xx UI surfaces.
//
// --------------------------------------------------------------------------
// SANITIZATION + SECURITY:
//   - SpreadSchema.safeParse defends the resolver path (Mission Critical
//     always-on; defense-in-depth for any payload reaching the form).
//   - Spread name + vocabularyOverrides are rendered as JSX text (no
//     dangerouslySetInnerHTML, no eval, no Function constructor).
//   - All inline styles are confined to SortableItem (transform/transition
//     carve-out) and SplitPane (gridTemplateColumns carve-out) per UI-SPEC
//     §"Sanitization & Security Contract".
//
// SplitPane storage-key note: the split-ratio key uses the project "trs:"
// namespace (The Recipe Spread); the key string lives on the SplitPane
// storageKey prop in SpreadComposerShell (W17 carry-forward from Phase 8).

import { useMutation, useQuery } from "@tanstack/react-query";
import { Spread as SpreadSchema, type Spread } from "@the-recipe-spread/shared";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  type FieldValues,
  FormProvider,
  type Resolver,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
// Plan 09-13 Rule 1 auto-fix: ConflictBadge + RecipeOptionGroup both use Radix
// Tooltip primitives which require a <Tooltip.Provider> ancestor. SpreadComposer
// is the production mount point; wrap once here so all nested tooltips have a
// provider in scope.
//
// Phase 11.7 Plan 03 (Surface-2) HOST CONFIRM: the new multi-batch-set UI lands
// entirely inside BatchSetStrip/BatchSetCard and is fully hosted by THIS tree
// with NO structural change required:
//   - the Duplicate-pill tooltip (enabled hint + the imported
//     DisabledOptionWithTooltip) resolves against this same TooltipProvider;
//   - the duplicate-name banner + Allow toggle + per-batch picker copy resolve
//     against the SpreadVocabularyProvider (useTerm) already wrapping the tree;
//   - the per-batch flavor picker scopes over the RHF `batchSets.{i}.selections`
//     field-array path under the existing FormProvider, so it needs NO
//     RecipeStateProvider scope (a DIFFERENT state binding than Surface 1's
//     RecipeStateContext). No provider is mounted at the card boundary.
import { TooltipProvider } from "@/components/ui/tooltip";
import { ApiError, api } from "@/lib/api-client";
import { computeAllowedReturnTo } from "@/lib/safe-return-to";
import { SpreadVocabularyProvider } from "@/lib/vocabulary/SpreadVocabularyProvider";
import { SpreadComposerShell } from "./SpreadComposerShell";
import { SpreadComposerToolbar } from "./SpreadComposerToolbar";
import { SpreadSettingsDialog } from "./SpreadSettingsDialog";

// --------------------------------------------------------------------------
// Empty-spread factory — schema-valid minimum so a brand-new spread mounts
// with no validation errors (mode="onBlur" silences them on first render).
// --------------------------------------------------------------------------
function makeEmptySpread(): Spread {
  return {
    // The id is a brand-new client-side placeholder. Plan 09-12 replaces it
    // with the server-issued id on first save. Mission Critical: nothing
    // sensitive flows from this default — it's overwritten before
    // round-tripping to the API.
    id: "new-spread",
    name: "",
    batchSets: [
      {
        // Minimum schema-valid BatchSet — Plan 09-12's recipe picker fills
        // in the real recipeId. The placeholder string here is sentinel: it
        // never reaches the server (the empty default is replaced before the
        // first save can fire because the form is invalid until the user
        // picks a real recipe).
        recipeId: "pending",
        batchSize: "1x",
      },
    ],
  };
}

// --------------------------------------------------------------------------
// SpreadComposerToolbarWrapper — per-path useWatch for the toolbar's
// spreadName prop. Lives inside SpreadComposer body so it has access to
// form context via useFormContext. Subscribes to ONLY 'name'. Mirrors
// RecipeEditor's EditorToolbarWrapper pattern.
// --------------------------------------------------------------------------
interface SpreadComposerToolbarWrapperProps {
  isSaving: boolean;
  onSave: () => void;
  onOpenSettings: () => void;
  onOpenShare: () => void;
}

function SpreadComposerToolbarWrapper(
  props: SpreadComposerToolbarWrapperProps,
): React.ReactElement {
  const { control, formState } = useFormContext();
  // W15 carry-forward: per-path useWatch ONLY. The instance-watch surface
  // skips renders under React 19 Compiler — useWatch is the supported path.
  const name = useWatch({ control, name: "name" }) as string | undefined;
  return (
    <SpreadComposerToolbar
      spreadName={name}
      isDirty={formState.isDirty}
      isSaving={props.isSaving}
      isValid={formState.isValid}
      onSave={props.onSave}
      onOpenSettings={props.onOpenSettings}
      onOpenShare={props.onOpenShare}
    />
  );
}

// --------------------------------------------------------------------------
// VocabularyProviderWrapper — per-path useWatch on vocabularyOverrides so
// the live-preview UI (UI-SPEC §"Vocabulary tab" line 441) re-renders the
// tab-strip header text on every keystroke. The wrapper is inside
// FormProvider so it has access to useFormContext.
// --------------------------------------------------------------------------
interface VocabularyProviderWrapperProps {
  children: React.ReactNode;
}

function VocabularyProviderWrapper({
  children,
}: VocabularyProviderWrapperProps): React.ReactElement {
  const { control } = useFormContext();
  // The form may include a top-level `vocabularyOverrides` field once the
  // SettingsDialog (Plan 09-12) ships. For Plan 09-11, no UI authors this
  // field directly; useWatch returns undefined → the provider sees `{}`.
  const overrides = useWatch({ control, name: "vocabularyOverrides" }) as
    | Record<string, string>
    | undefined;
  return <SpreadVocabularyProvider value={overrides ?? {}}>{children}</SpreadVocabularyProvider>;
}

// --------------------------------------------------------------------------
// SpreadComposer — top-level page.
//
// Tests pass an `initialSpread` prop to seed the form deterministically.
// Production resolves the spread by URL param via TanStack Query (Plan 09-12
// fills in the queryFn body — Plan 09-11 stubs it to return null).
// --------------------------------------------------------------------------
export interface SpreadComposerProps {
  initialSpread?: Spread | undefined;
}

export function SpreadComposer(props: SpreadComposerProps): React.ReactElement {
  const params = useParams<{ spreadId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const spreadId = params.spreadId;

  // Server-fetch query. Disabled when we already have an initialSpread
  // (tests) or when we're authoring a brand-new spread (/spread/new).
  const enableQuery =
    props.initialSpread === undefined && typeof spreadId === "string" && spreadId.length > 0;
  const spreadQuery = useQuery<Spread | null>({
    queryKey: ["spread", spreadId],
    enabled: enableQuery,
    queryFn: async () => {
      // Plan 09-12: GET /api/spreads/:id, then validate the response envelope.
      // The API returns { id, owner_id, slug, visibility, body, vocabulary_overrides }
      // where `body` is the canonical Spread shape. We parse `body` through
      // SpreadSchema to defend the resolver path (Mission Critical defense-in-
      // depth) and surface the vocabularyOverrides at the form level under
      // the top-level path `vocabularyOverrides` so the dialog + provider
      // wrapper both subscribe to a single, well-known path.
      const envelope = await api<{
        id: string;
        owner_id?: string;
        slug?: string;
        visibility?: string;
        body: unknown;
        vocabulary_overrides?: Record<string, string>;
      }>(`/api/spreads/${spreadId}`);
      const parsed = SpreadSchema.safeParse(envelope.body);
      if (!parsed.success) {
        // The server-side adapter validates Spread bodies on persist; a
        // mismatch here is exceptional (drift or DB tampering). Throw with
        // a 422-shaped error so the existing error UI surfaces it.
        throw new ApiError(422, parsed.error.issues, "Spread body failed validation");
      }
      // Stitch vocabulary_overrides into the form-level shape used by the
      // SettingsDialog/VocabularyTab/VocabularyProviderWrapper trio.
      const formShape = {
        ...parsed.data,
        vocabularyOverrides: envelope.vocabulary_overrides ?? {},
        visibility: envelope.visibility,
      } as unknown as Spread;
      return formShape;
    },
  });

  const initialDefaults: Spread =
    props.initialSpread ?? spreadQuery.data ?? makeEmptySpread();

  // Custom Zod resolver — mirrors RecipeEditor.tsx pattern. The form values
  // are typed as `FieldValues` (NOT `Spread`) so the resolver type lines up
  // without an `as unknown as` cast on the schema itself.
  const spreadResolver: Resolver<FieldValues> = useCallback(async (values) => {
    const result = SpreadSchema.safeParse(values);
    if (result.success) {
      return { values: result.data as unknown as FieldValues, errors: {} };
    }
    type ErrorEntry = { type: string; message: string };
    const errors: Record<string, ErrorEntry> = {};
    for (const issue of result.error.issues) {
      const key = issue.path.join(".");
      if (errors[key] === undefined) {
        errors[key] = { type: "zod", message: issue.message };
      }
    }
    return { values: {}, errors };
  }, []);

  const form = useForm<FieldValues>({
    resolver: spreadResolver,
    defaultValues: initialDefaults as unknown as FieldValues,
    mode: "onBlur",
  });

  // Re-seed defaults when a deferred spreadQuery lands.
  useEffect(() => {
    if (props.initialSpread !== undefined) return;
    if (spreadQuery.data !== undefined && spreadQuery.data !== null) {
      form.reset(spreadQuery.data as unknown as FieldValues);
    }
  }, [spreadQuery.data, props.initialSpread, form.reset]);

  const [serverError, setServerError] = useState<{
    code: 403 | 404;
    message: string;
  } | null>(null);

  // -------------------------------------------------------------------------
  // Save mutation — Plan 09-12. POST a brand-new spread, PUT an existing one.
  // The mutation accepts the form-level payload (which includes vocabulary-
  // Overrides at the top level); we split it into the API envelope shape
  // (body + vocabulary_overrides + name + slug + visibility) right before the
  // fetch. ApiError from the api() wrapper carries the HTTP status which the
  // onError handler maps to the existing 401/403/404/422/5xx UI surfaces.
  // -------------------------------------------------------------------------
  const saveMutation = useMutation<Spread, Error, Spread>({
    mutationFn: async (spread) => {
      // The form-state shape carries vocabularyOverrides + visibility at the
      // top level (alongside the canonical Spread body fields). Split them
      // out for the API envelope.
      const formShape = spread as unknown as Spread & {
        vocabularyOverrides?: Record<string, string>;
        visibility?: "private" | "public" | "shared";
      };
      const { vocabularyOverrides, visibility, ...body } = formShape;
      const slug = (formShape as unknown as { slug?: string }).slug ?? slugifyName(formShape.name);
      const envelope = {
        name: formShape.name,
        slug,
        visibility: visibility ?? "private",
        body,
        vocabulary_overrides: vocabularyOverrides,
      };

      let response: {
        id: string;
        owner_id?: string;
        slug?: string;
        visibility?: string;
        body?: unknown;
        vocabulary_overrides?: Record<string, string>;
      };
      if (spreadId === undefined || spreadId.length === 0 || spreadId === "new-spread") {
        response = await api(`/api/spreads`, {
          method: "POST",
          body: JSON.stringify(envelope),
        });
      } else {
        response = await api(`/api/spreads/${spreadId}`, {
          method: "PUT",
          body: JSON.stringify(envelope),
        });
      }

      // Stitch the server response back into the form shape. POST returns
      // only { id, slug }; PUT returns the full body. Either way, prefer the
      // server-supplied fields and fall back to the submitted ones for the
      // POST path (so a successful POST leaves the form clean with the new id).
      const stitched: Spread = {
        ...body,
        id: response.id,
        name: formShape.name,
      } as Spread;
      return {
        ...stitched,
        vocabularyOverrides: response.vocabulary_overrides ?? vocabularyOverrides ?? {},
        visibility: response.visibility ?? visibility ?? "private",
      } as Spread;
    },
    onSuccess: (saved) => {
      setServerError(null);
      form.reset(saved as unknown as FieldValues);
      // For new spreads the server response would carry the new id — Plan 09-12
      // wires the navigate() to /spread/:newId on first save.
      const newIdCandidate = (saved as { id?: unknown }).id;
      if (
        (spreadId === undefined || spreadId.length === 0) &&
        typeof newIdCandidate === "string" &&
        newIdCandidate.length > 0 &&
        newIdCandidate !== "new-spread"
      ) {
        navigate(`/spread/${newIdCandidate}`, { replace: true });
      }
    },
    onError: (err) => {
      // ApiError (from @/lib/api-client) carries the HTTP status; legacy
      // callers may attach a `status` directly. Read both.
      const status: number | undefined =
        err instanceof ApiError ? err.status : (err as Error & { status?: number }).status;
      if (status === undefined) {
        toast.error("Couldn't save spread — check your connection and try again.");
        return;
      }
      switch (status) {
        case 401: {
          const safeReturn = computeAllowedReturnTo(location.pathname);
          navigate(`/auth/sign-in?returnTo=${encodeURIComponent(safeReturn)}`);
          return;
        }
        case 403:
          setServerError({
            code: 403,
            message: "You no longer have permission to save this spread.",
          });
          return;
        case 404:
          setServerError({ code: 404, message: "Spread not found." });
          return;
        case 422: {
          const details = (err as Error & { details?: unknown }).details as
            | { issues?: Array<{ path: Array<string | number>; message: string }> }
            | undefined;
          const issues = details?.issues ?? [];
          if (issues.length === 0) {
            toast.error("Couldn't save spread — the server rejected one or more fields.");
          }
          for (const issue of issues) {
            const path = issue.path.join(".");
            form.setError(path, { type: "server", message: issue.message });
          }
          return;
        }
        default:
          toast.error("Couldn't save spread — the server returned an error.");
      }
    },
  });

  // -------------------------------------------------------------------------
  // Handlers — Save / Settings / Share.
  // -------------------------------------------------------------------------
  const onSave = (): void => {
    void form.handleSubmit((values) =>
      saveMutation.mutateAsync(values as unknown as Spread),
    )();
  };

  // Plan 09-12: SpreadSettingsDialog open + default-tab state. Both the
  // Settings button and the Share button open the same dialog; Share lands
  // on the Sharing tab, Settings on whichever tab the dialog defaults to.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDefaultTab, setSettingsDefaultTab] = useState<"sharing" | "vocabulary">(
    "sharing",
  );

  const onOpenSettings = (): void => {
    setSettingsDefaultTab("vocabulary");
    setSettingsOpen(true);
  };

  const onOpenShare = (): void => {
    setSettingsDefaultTab("sharing");
    setSettingsOpen(true);
  };

  const onCreateNewSpread = (): void => {
    // STUBBED — Plan 09-12 wires navigation to /spread/new and discards the
    // current form if dirty (with UnsavedChangesGuard).
    // eslint-disable-next-line no-console
    console.warn("[SpreadComposer] Create-new-spread stubbed (Plan 09-12 wires this)");
  };

  // -------------------------------------------------------------------------
  // Loading + error early returns.
  // -------------------------------------------------------------------------
  if (enableQuery && spreadQuery.isLoading) {
    return (
      <main aria-label="Spread composer" className="flex h-screen items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span>Loading spread…</span>
        </div>
      </main>
    );
  }

  if (enableQuery && spreadQuery.isError) {
    return (
      <main aria-label="Spread composer" className="mx-auto max-w-md p-8">
        <Alert variant="destructive">
          <AlertTitle>Couldn't load the spread</AlertTitle>
          <AlertDescription>
            {spreadQuery.error instanceof Error ? spreadQuery.error.message : "Unknown error"}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  // SpreadComposerShell renders <main aria-label="Spread composer">; we wrap
  // in a fragment here so the page tree contains exactly ONE main landmark.
  return (
    <FormProvider {...form}>
      <TooltipProvider>
      <VocabularyProviderWrapper>
        {serverError !== null ? (
          <Alert variant="destructive" className="m-4">
            <AlertTitle>
              {serverError.code === 403 ? "Permission denied" : "Spread not found"}
            </AlertTitle>
            <AlertDescription>{serverError.message}</AlertDescription>
          </Alert>
        ) : null}
        <SpreadComposerShell
          spreadId={spreadId ?? "new-spread"}
          onCreateNewSpread={onCreateNewSpread}
          toolbar={
            <SpreadComposerToolbarWrapper
              isSaving={saveMutation.isPending}
              onSave={onSave}
              onOpenSettings={onOpenSettings}
              onOpenShare={onOpenShare}
            />
          }
        />
        <SpreadSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          spreadId={spreadId ?? "new-spread"}
          defaultTab={settingsDefaultTab}
        />
      </VocabularyProviderWrapper>
      </TooltipProvider>
    </FormProvider>
  );
}

// --------------------------------------------------------------------------
// slugifyName — derive a URL-safe slug from the spread name when the form
// doesn't carry one explicitly. The API requires a non-empty slug; this
// guarantees the POST payload validates without making the user pick one.
// Mission Critical: the slug is rendered server-side as a path parameter,
// so we only allow [a-z0-9-]+ (everything else stripped, runs of `-` collapsed).
// --------------------------------------------------------------------------
function slugifyName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  return slug.length > 0 ? slug : "untitled-spread";
}
SpreadComposer.displayName = "SpreadComposer";
